import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { formatMoney, moneyFromCents } from '@wodi/domain';
import type { ClaimDetail, ClaimListView, ClaimSummary } from '@wodi/shared';
import {
  Button,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Select,
  Tag,
  TextField,
  Section,
} from '@wodi/ui';
import { useState } from 'react';

import {
  askWriteOff,
  assignClaimToMe,
  checkClaimStatus,
  claimKeys,
  claimListQuery,
  claimQuery,
  decideWriteOff,
  preauthListQuery,
  submitClaim,
  type WriteOffReason,
} from '../api/claims.ts';
import { can, sessionQuery } from '../api/session.ts';
import billing from '../billing/billing.module.css';
import { useOnline } from '../hooks/use-online.ts';
import { screen, TableScroll } from '../layout/screen.tsx';
import { formatDateOnly, formatInstant } from '../patients/format.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { ConnectionNote } from '../sha/sha-cover.tsx';
import styles from '../sha/sha.module.css';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// Claims (docs/plans/phase1-sha.md §9.4, §9.5, §7.10): the work queue opens on "Needs
// attention" (rejected first, then returned and unsent, then drafts), each claim's state as
// a sentence with SHA's reason in SHA's words, and "Fix" links to where each problem is
// fixed. Pre-authorisations are a view under Claims (plan P12).

const listRoute = getRouteApi('/_app/claims');

const VIEWS: { id: ClaimListView; label: string }[] = [
  { id: 'needs_attention', label: 'Needs attention' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'settled', label: 'Settled' },
  { id: 'all', label: 'All' },
];

const EMPTY: Record<ClaimListView, string> = {
  needs_attention: 'No claims need attention. Rejected and unsent claims appear here.',
  in_progress: 'No claims are with SHA right now.',
  settled: 'No claims have been paid or written off yet.',
  all: 'No SHA claims yet. They are prepared when SHA visits close.',
};

const kes = (cents: string | null) =>
  cents === null ? '' : formatMoney(moneyFromCents(BigInt(cents)));

/** Where each readiness problem is fixed (CLAIM_READINESS_FIX in @wodi/domain). */
function FixLink({ fix, patientId }: { fix: string; patientId: string }) {
  if (fix === 'settings.practitioners') {
    return <Link to="/settings/practitioners">Fix in Settings, Practitioners</Link>;
  }
  if (fix.startsWith('settings.sha')) {
    return <Link to="/settings/sha">Fix in Settings, SHA</Link>;
  }
  if (fix === 'catalogue.item_codes')
    return <Link to="/settings/services">Fix in Services and prices</Link>;
  if (fix === 'visit.preauth') return <Link to="/claims/preauth">Open pre-authorisations</Link>;
  if (fix === 'billing.not_priced' || fix === 'visit.bill')
    return <Link to="/cashier">Open the bill</Link>;
  return (
    <Link to="/patients/$patientId" params={{ patientId }}>
      Open the patient
    </Link>
  );
}

function ClaimTabs({ current }: { current: ClaimListView | 'preauth' }) {
  return (
    <nav aria-label="Claim views" className={screen.tabs}>
      {VIEWS.map((v) => (
        <Link
          key={v.id}
          to="/claims"
          search={{ view: v.id }}
          activeOptions={{ exact: true }}
          {...(current === v.id ? { 'aria-current': 'page' as const } : {})}
        >
          {v.label}
        </Link>
      ))}
      <Link
        to="/claims/preauth"
        {...(current === 'preauth' ? { 'aria-current': 'page' as const } : {})}
      >
        Pre-authorisations
      </Link>
    </nav>
  );
}

function ClaimRow({ claim }: { claim: ClaimSummary }) {
  return (
    <tr>
      <td>
        <Link to="/claims/$claimId" params={{ claimId: claim.id }} className={styles.id}>
          {claim.claimNumber}
        </Link>
      </td>
      <td>
        {claim.patient.displayName} <span className={styles.id}>{claim.patient.patientNumber}</span>
      </td>
      <td>{formatDateOnly(claim.visit.startedAt)}</td>
      <td>{claim.schemeName}</td>
      <td className={styles.number}>{kes(claim.claimedCents)}</td>
      <td>
        {claim.sentence}
        {claim.problems.length > 1 ? (
          <span className={styles.detail}>{` and ${String(claim.problems.length - 1)} more`}</span>
        ) : null}
      </td>
      <td>{claim.assignedTo?.displayName ?? ''}</td>
    </tr>
  );
}

export function ClaimsPage() {
  const { view } = listRoute.useSearch();
  const online = useOnline();
  const list = useQuery({ ...claimListQuery(view), refetchInterval: 30_000 });
  return (
    <div className={billing.page}>
      <PageHeader title="Claims" />
      <ConnectionNote />
      <ClaimTabs current={view} />
      {!online && list.isSuccess ? (
        <Notice tone="info">{`Offline, showing claims loaded at ${formatInstant(new Date(list.dataUpdatedAt).toISOString())}.`}</Notice>
      ) : null}
      {list.isPending ? <LoadingBlock label="Loading claims" rows={4} /> : null}
      {list.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void list.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(list.error, "Claims couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? (
        <EmptyState>{EMPTY[view]}</EmptyState>
      ) : null}
      {list.isSuccess && list.data.items.length > 0 ? (
        <div className={styles.claimsList}>
          <TableScroll>
            <table className={styles.table} aria-label="Claims">
              <thead>
                <tr>
                  <th scope="col">Claim</th>
                  <th scope="col">Patient</th>
                  <th scope="col">Visit</th>
                  <th scope="col">Scheme</th>
                  <th scope="col" className={styles.number}>
                    Claimed
                  </th>
                  <th scope="col">State</th>
                  <th scope="col">Assigned to</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((claim) => (
                  <ClaimRow key={claim.id} claim={claim} />
                ))}
              </tbody>
            </table>
          </TableScroll>
        </div>
      ) : null}
    </div>
  );
}

const claimRoute = getRouteApi('/_app/claims/$claimId');

const REASON_WORDS: Record<WriteOffReason, string> = {
  sha_final_rejection: 'SHA rejected it for good',
  past_deadline: 'Past the submission deadline',
  not_claimable: 'Not claimable',
  other: 'Other',
};

function WriteOffForm({ claim, onDone }: { claim: ClaimDetail; onDone: (c: ClaimDetail) => void }) {
  const open = BigInt(claim.claimedCents ?? '0');
  const [amount, setAmount] = useState(open.toString());
  const [reason, setReason] = useState<WriteOffReason>('sha_final_rejection');
  const [note, setNote] = useState('');
  const ask = useMutation({
    mutationFn: () =>
      askWriteOff(claim.id, {
        amountCents: amount,
        reasonCode: reason,
        ...(note.trim() === '' ? {} : { reasonNote: note.trim() }),
      }),
    onSuccess: (c) => {
      toasts.show({ title: 'Write-off asked for' });
      onDone(c);
    },
  });
  return (
    <form
      className={styles.form}
      aria-label="Ask to write off"
      onSubmit={(e) => {
        e.preventDefault();
        ask.mutate();
      }}
    >
      <TextField
        label="Amount in cents"
        description={`At most ${kes(open.toString())}, what was claimed and not paid.`}
        inputMode="numeric"
        value={amount}
        onChange={setAmount}
      />
      <Select
        label="Reason"
        options={(Object.keys(REASON_WORDS) as WriteOffReason[]).map((r) => ({
          id: r,
          label: REASON_WORDS[r],
        }))}
        value={reason}
        onChange={(v) => {
          setReason(String(v) as WriteOffReason);
        }}
      />
      <TextField label="Note" description="Required for Other." value={note} onChange={setNote} />
      {ask.isError ? (
        <Notice tone="error">{errorText(ask.error, "The write-off couldn't be asked for.")}</Notice>
      ) : null}
      <div className={styles.actions}>
        <Button type="submit" isPending={ask.isPending} pendingLabel="Asking…">
          Ask to write off
        </Button>
      </div>
    </form>
  );
}

function ClaimActions({ claim }: { claim: ClaimDetail }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const [writingOff, setWritingOff] = useState(false);
  const update = (c: ClaimDetail) => {
    queryClient.setQueryData(claimKeys.detail(claim.id), c);
    void queryClient.invalidateQueries({ queryKey: claimKeys.all });
  };
  const submit = useMutation({
    mutationFn: (resubmit: boolean) => submitClaim(claim.id, claim.version, resubmit),
    onSuccess: (c) => {
      update(c);
      toasts.show({ title: 'Claim queued to send to SHA' });
    },
  });
  const assign = useMutation({ mutationFn: () => assignClaimToMe(claim.id), onSuccess: update });
  const check = useMutation({ mutationFn: () => checkClaimStatus(claim.id), onSuccess: update });
  const canSubmit = session !== null && can(session, 'claims.submit');
  const canWriteOff = session !== null && can(session, 'claims.write_off.request');
  const writeOffable = ['rejected', 'returned', 'partly_approved', 'send_failed'].includes(
    claim.state,
  );
  const error = submit.error ?? assign.error ?? check.error;
  return (
    <section className={billing.section} aria-label="Claim actions">
      <div className={styles.actions}>
        {canSubmit && claim.canSubmit ? (
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={submit.isPending}
            pendingLabel="Sending…"
            onPress={() => {
              submit.mutate(false);
            }}
          >
            Submit claim
          </Button>
        ) : null}
        {canSubmit && claim.canResubmit ? (
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={submit.isPending}
            pendingLabel="Sending…"
            onPress={() => {
              submit.mutate(true);
            }}
          >
            Submit again
          </Button>
        ) : null}
        {canSubmit &&
        claim.workState !== 'fixing' &&
        (claim.workState === 'needs_fix' || claim.state === 'draft') ? (
          <Button
            isDisabled={!online}
            isPending={assign.isPending}
            pendingLabel="Assigning…"
            onPress={() => {
              assign.mutate();
            }}
          >
            Assign to me
          </Button>
        ) : null}
        {claim.externalReference !== null ? (
          <Button
            isDisabled={!online}
            isPending={check.isPending}
            pendingLabel="Checking…"
            onPress={() => {
              check.mutate();
            }}
          >
            Check status
          </Button>
        ) : null}
        {canWriteOff && writeOffable && !writingOff ? (
          <Button
            onPress={() => {
              setWritingOff(true);
            }}
          >
            Ask to write off
          </Button>
        ) : null}
        {claim.submissions.length > 0 ? (
          <a
            href={`/api/v1/claims/${claim.id}/submissions/${String(claim.submissions.length)}/bundle`}
            download
          >
            Download what was sent
          </a>
        ) : null}
      </div>
      {!online ? <Notice tone="info">Sending and checking claims need a connection.</Notice> : null}
      {error !== null ? (
        <Notice tone="error">{errorText(error, 'That didn’t work. Try again.')}</Notice>
      ) : null}
      {writingOff ? (
        <WriteOffForm
          claim={claim}
          onDone={(c) => {
            update(c);
            setWritingOff(false);
          }}
        />
      ) : null}
    </section>
  );
}

function WriteOffs({ claim }: { claim: ClaimDetail }) {
  const queryClient = useQueryClient();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const decide = useMutation({
    mutationFn: (v: { id: string; decision: 'approve' | 'reject' }) =>
      decideWriteOff(v.id, v.decision),
    onSuccess: (c) => {
      queryClient.setQueryData(claimKeys.detail(claim.id), c);
      toasts.show({ title: 'Write-off decided' });
    },
  });
  if (claim.writeOffs.length === 0) return null;
  const mayDecide = session !== null && can(session, 'claims.write_off.approve');
  return (
    <Section title="Write-offs">
      <ul className={styles.schemes}>
        {claim.writeOffs.map((w) => (
          <li key={w.id} className={styles.scheme}>
            <span className={styles.schemeLine}>{kes(w.amountCents)}</span>
            <span>{REASON_WORDS[w.reasonCode as WriteOffReason]}</span>
            <Tag
              state={
                w.status === 'requested'
                  ? 'pending'
                  : w.status === 'approved'
                    ? 'done'
                    : w.status === 'rejected'
                      ? 'failed'
                      : 'neutral'
              }
            >
              {w.status === 'requested'
                ? 'Waiting for a second person'
                : w.status === 'approved'
                  ? 'Approved'
                  : w.status === 'rejected'
                    ? 'Rejected'
                    : 'Withdrawn'}
            </Tag>
            <span
              className={styles.detail}
            >{`Asked by ${w.requestedBy}${w.decidedBy === null ? '' : `, decided by ${w.decidedBy}`}`}</span>
            {mayDecide && w.status === 'requested' ? (
              <span className={styles.actions}>
                <Button
                  dense
                  onPress={() => {
                    decide.mutate({ id: w.id, decision: 'approve' });
                  }}
                >
                  Approve
                </Button>
                <Button
                  dense
                  onPress={() => {
                    decide.mutate({ id: w.id, decision: 'reject' });
                  }}
                >
                  Reject
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {decide.isError ? (
        <Notice tone="error">
          {errorText(decide.error, "The write-off couldn't be decided.")}
        </Notice>
      ) : null}
    </Section>
  );
}

function ClaimBody({ claim }: { claim: ClaimDetail }) {
  return (
    <>
      <PatientBannerLoader patientId={claim.patient.id} />
      <p>
        <span className={styles.id}>{claim.claimNumber}</span>
        {` · ${claim.schemeName} · visit `}
        <span className={styles.id}>{claim.visit.visitNumber}</span>
      </p>
      <p className={styles.schemeLine}>{claim.sentence}</p>
      {claim.problems.length > 0 ? (
        <Section title="To fix before sending">
          <ul className={styles.schemes}>
            {claim.problems.map((p) => (
              <li key={`${p.code}|${p.subjectId ?? ''}`} className={styles.scheme}>
                <span>{p.words}</span>
                <FixLink fix={p.fix} patientId={claim.patient.id} />
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {claim.reasons.length > 0 ? (
        <Section title="SHA's reasons">
          <ul className={styles.reasons}>
            {claim.reasons.map((r) => (
              <li key={r.code}>
                {r.display}
                {r.element === null ? null : (
                  <span className={styles.detail}>{` (about ${r.element})`}</span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      <ClaimActions claim={claim} />
      {claim.lines.length > 0 ? (
        <TableScroll>
          <table className={styles.table} aria-label="Lines by intervention">
            <thead>
              <tr>
                <th scope="col">Code</th>
                <th scope="col">Intervention</th>
                <th scope="col" className={styles.number}>
                  Quantity
                </th>
                <th scope="col" className={styles.number}>
                  SHA pays
                </th>
                <th scope="col" className={styles.number}>
                  Patient pays
                </th>
                <th scope="col">Date</th>
                <th scope="col">Pre-authorisation</th>
              </tr>
            </thead>
            <tbody>
              {claim.lines.map((l) => (
                <tr key={l.sequence}>
                  <td className={styles.id}>{l.code}</td>
                  <td>{l.display}</td>
                  <td className={styles.number}>{l.quantity}</td>
                  <td className={styles.number}>{kes(l.payerCents)}</td>
                  <td className={styles.number}>{kes(l.patientCents)}</td>
                  <td>{l.serviceDate}</td>
                  <td>{l.preauth?.words ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      ) : null}
      {claim.diagnoses.length > 0 ? (
        <ul className={styles.schemes} aria-label="Diagnoses">
          {claim.diagnoses.map((d) => (
            <li key={d.sequence} className={styles.scheme}>
              <span className={styles.id}>{d.code}</span> <span>{d.display}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <WriteOffs claim={claim} />
      <Section title="History">
        <ol className={styles.schemes}>
          {claim.events.map((e, i) => (
            <li key={`${e.occurredAt}|${String(i)}`} className={styles.scheme}>
              <span className={styles.detail}>{formatInstant(e.occurredAt)}</span>
              <span>{e.words}</span>
              <span className={styles.detail}>{e.actor}</span>
              {e.reasons.map((r) => (
                <span key={r.code} className={styles.detail}>
                  {r.display}
                </span>
              ))}
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}

export function ClaimPage() {
  const { claimId } = claimRoute.useParams();
  const claim = useQuery(claimQuery(claimId));
  return (
    <div className={billing.page}>
      <PageHeader title="Claim" />
      <ConnectionNote />
      {claim.isPending ? <LoadingBlock label="Loading the claim" rows={4} /> : null}
      {claim.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void claim.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(claim.error, "The claim couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {claim.isSuccess ? <ClaimBody claim={claim.data} /> : null}
    </div>
  );
}

export function PreauthPage() {
  const list = useQuery({ ...preauthListQuery, refetchInterval: 30_000 });
  return (
    <div className={billing.page}>
      <PageHeader title="Claims" />
      <ConnectionNote />
      <ClaimTabs current="preauth" />
      {list.isPending ? <LoadingBlock label="Loading pre-authorisations" rows={3} /> : null}
      {list.isError ? (
        <Notice tone="error">
          {errorText(list.error, "Pre-authorisations couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? (
        <EmptyState>
          No pre-authorisations yet. Services SHA must approve are asked for from the order.
        </EmptyState>
      ) : null}
      {list.isSuccess && list.data.items.length > 0 ? (
        <TableScroll>
          <table className={styles.table} aria-label="Pre-authorisations">
            <thead>
              <tr>
                <th scope="col">Request</th>
                <th scope="col">Service</th>
                <th scope="col">Asked</th>
                <th scope="col">State</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((p) => (
                <tr key={p.id}>
                  <td className={styles.id}>{p.preauthNumber}</td>
                  <td>
                    <span className={styles.id}>{p.interventionCode}</span> {p.interventionDisplay}
                  </td>
                  <td>{formatInstant(p.requestedAt)}</td>
                  <td>{p.sentence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      ) : null}
    </div>
  );
}
